import { Dialog } from '@base-ui/react/dialog'
import type { ReactElement, ReactNode } from 'react'
import { cx } from '../cx.js'
import styles from './Sheet.module.scss'

/**
 * Sheet — the container Trace (M3.5) and the correction sheet (M4.1) live in.
 *
 * Wraps `@base-ui/react` Dialog, which supplies `role="dialog"`, `aria-modal`,
 * labelling by the title, the focus trap, scroll lock, Escape to close and
 * focus return to the opener. This file adds the layout — a bottom sheet
 * below the `md` breakpoint, a side sheet from it — with a pinned header and
 * action row around a scrolling body, and the one motion the sheet is
 * allowed: opening and closing, at token durations, instant under reduced
 * motion (the duration tokens are zero there).
 */
export type SheetProps = {
  /** The heading. It also names the dialog for assistive technology. */
  title: string
  /** One short line under the heading, when the sheet needs one. */
  description?: string | undefined
  /**
   * The element that opens the sheet. It receives the trigger's props
   * (`aria-haspopup`, `aria-expanded`, the click handler) and must forward
   * them to a DOM element. Omit it for a sheet driven by `open` alone.
   */
  trigger?: ReactElement | undefined
  /** Controlled state, for sheets opened from a menu or a card action. */
  open?: boolean | undefined
  defaultOpen?: boolean | undefined
  onOpenChange?: ((open: boolean) => void) | undefined
  /** Pinned below the body: the smallest useful actions for the section. */
  actions?: ReactNode
  /** Accessible name of the close button in the header. */
  closeLabel?: string | undefined
  children: ReactNode
}

export function Sheet({
  title,
  description,
  trigger,
  open,
  defaultOpen,
  onOpenChange,
  actions,
  closeLabel = 'Close',
  children,
}: SheetProps) {
  return (
    <Dialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} modal>
      {trigger ? <Dialog.Trigger render={trigger} /> : null}
      <Dialog.Portal>
        <Dialog.Backdrop
          className={(state) =>
            cx(
              styles.backdrop,
              state.transitionStatus === 'starting' && styles.isEntering,
              state.transitionStatus === 'ending' && styles.isLeaving,
            )
          }
        />
        <Dialog.Popup
          aria-modal="true"
          className={(state) =>
            cx(
              styles.root,
              state.open && styles.isOpen,
              state.transitionStatus === 'starting' && styles.isEntering,
              state.transitionStatus === 'ending' && styles.isLeaving,
            )
          }
        >
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>{title}</Dialog.Title>
            {description ? (
              <Dialog.Description className={styles.description}>{description}</Dialog.Description>
            ) : null}
            <Dialog.Close className={styles.close} aria-label={closeLabel}>
              <CloseGlyph />
            </Dialog.Close>
          </header>
          <div className={styles.body}>{children}</div>
          {actions ? <footer className={styles.actions}>{actions}</footer> : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

/** The close ×: one of the two glyphs the direction allows without a label. */
function CloseGlyph() {
  return (
    <svg className={styles.closeGlyph} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path
        d="M5 5l10 10M15 5L5 15"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  )
}
